/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { memo } from 'react';
import { EntryPointContainer } from 'react-relay';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { EntryPointReferencePlaceholder } from '@jira/platform__entry-points-point-placeholder/src/index.tsx';
import {
	JiraEntryPointContainer,
	type Props,
} from '@jira/platform__entry-points-container/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import type { AnyEntryPoint } from '@jira/platform__entry-point/src/common/types.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import type { EntryPointReferenceSubject } from '@jira/platform__entry-point/src/controllers/utils/use-entry-point-load-manager/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { JiraHoldoutCustomData } from '@atlassian/holdout-utils/jira-holdout';

type EntryPointAdapterProps<TEntryPoint extends AnyEntryPoint> = {
	epContainerProps: Omit<Props<TEntryPoint>, 'entryPointReferenceSubject'>;
	jiraEPRef: EntryPointReferenceSubject<TEntryPoint>;
	nestedEPRef?: never;
	placeholderEntryPoint: TEntryPoint;
	shouldRenderNestedEntryPoint?: boolean;
};

/**
 * This component is a Route-EntryPoint adapter. It is designed to support the situation
 * where a route is using entryPoints, which means that the route *might* be referencing
 * a left-sidebar entryPoint, which may subsequently be pre-loading a menu-component entryPoint.
 */
export const RouteEntryPointContainer: React.MemoExoticComponent<
	<TEntryPoint extends AnyEntryPoint>({
		epContainerProps,
		jiraEPRef,
		nestedEPRef,
		shouldRenderNestedEntryPoint,
	}: EntryPointAdapterProps<TEntryPoint>) => React.JSX.Element
> = memo(
	<TEntryPoint extends AnyEntryPoint>({
		epContainerProps,
		jiraEPRef,
		nestedEPRef,
		shouldRenderNestedEntryPoint = true,
	}: EntryPointAdapterProps<TEntryPoint>): React.JSX.Element => {
		// If the route is an entryPoint, and the initialRoute was an entryPoint, use EntryPointContainer.
		// Otherwise, use the JiraEntryPointContainer.
		return shouldRenderNestedEntryPoint && nestedEPRef ? (
			// eslint-disable-next-line jira/ufo/valid-labels
			<UFOSegment name={`${epContainerProps.id}`}>
				<JiraHoldoutCustomData />
				<JSErrorBoundary
					attributes={epContainerProps.errorAttributes}
					fallback={epContainerProps.errorFallback}
					id={epContainerProps.id}
					packageName={epContainerProps.packageName}
					teamName={epContainerProps.teamName}
				>
					<EntryPointReferencePlaceholder
						name={`${epContainerProps.id || 'route-entrypoint-adapter'}-entrypoint-placeholder`}
						fallback={epContainerProps.fallback}
						entryPointReference={nestedEPRef}
					>
						<EntryPointContainer
							entryPointReference={nestedEPRef}
							// oxlint-disable-next-line typescript/consistent-type-assertions
							props={{} as never}
						/>
					</EntryPointReferencePlaceholder>
				</JSErrorBoundary>
			</UFOSegment>
		) : (
			<JiraEntryPointContainer {...epContainerProps} entryPointReferenceSubject={jiraEPRef} />
		);
	},
);
